import { useRef } from 'react';
import { formatCurrency } from '@atc/string-fns';
import 'prop-types';
import { SideBarToModal, Text, LoadingSpinner, Button, ErrorMessages } from 'reaxl';
import { StandardZipInput } from 'reaxl-forms';
import { useDevice } from '@atc/react-device';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
function TaxesFeesModal(_ref) {
  let {
    activeTab = 'finance',
    container,
    // For Syndicated Calc
    zip,
    taxesFees = {},
    onHide = () => {},
    onContinue = () => {},
    onUpdate = () => {},
    isEstimate,
    showModal = false,
    isLoading = false,
    hasError = false
  } = _ref;
  const device = useDevice();
  const isXs = device?.is?.xs ?? false;
  const userZip = useRef(zip);
  const isLease = activeTab === 'lease';
  const {
    capCost = {},
    dueInAdvance = {}
  } = taxesFees;
  const {
    capitalizedDealerFees,
    capitalizedGovFees,
    capitalizedLenderFees,
    capitalizedTaxes,
    capitalizedTaxesAndFees
  } = capCost;
  const {
    diaDealerFees,
    diaGovFees,
    diaLenderFees,
    diaTaxes
  } = dueInAdvance;
  const totalDiaTaxesFees = (diaTaxes ?? 0) + (diaLenderFees ?? 0) + (diaGovFees ?? 0) + (diaDealerFees ?? 0);
  const showTotalTaxesOnly = !!taxesFees.showTaxTotalOnly; // for Esntial UDS only

  const handleSubmit = event => {
    onHide();
    onContinue(userZip.current, event);
  };
  const handleChange = e => {
    const updatedZip = e.target.value;
    if (updatedZip.length === 5) {
      userZip.current = updatedZip;
      onUpdate(updatedZip);
    }
  };
  const continueButton = /*#__PURE__*/jsx(Button, {
    type: "button",
    bsStyle: "primary",
    onClick: handleSubmit,
    disabled: hasError,
    children: "Apply Taxes & Fees"
  });
  const renderTotalTaxesFees = (label, total, classname) => !showTotalTaxesOnly && !!total && /*#__PURE__*/jsxs("div", {
    className: classname,
    children: [/*#__PURE__*/jsx(Text, {
      weight: isLease ? 'bold' : undefined,
      children: label
    }), /*#__PURE__*/jsx(Text, {
      weight: "bold",
      children: `${formatCurrency(Math.floor(total))}`
    })]
  });
  const renderLeaseFeesList = (fees, classnames) => fees.map(_ref2 => {
    let {
      key,
      label,
      value
    } = _ref2;
    return !!value && /*#__PURE__*/jsxs("li", {
      className: classnames,
      children: [/*#__PURE__*/jsx("span", {
        children: label
      }), /*#__PURE__*/jsx(Text, {
        children: `+ ${formatCurrency(Math.floor(value))}`
      })]
    }, key);
  });
  const renderFeesList = () => {
    if (hasError) {
      return /*#__PURE__*/jsx(ErrorMessages, {
        errors: ["We're sorry. We couldn't calculate taxes & fees for your location. Talk to the dealer about final calculations."]
      });
    }
    const rowClassnames = 'display-flex justify-content-between gap-3';
    const capTaxesFees = [{
      key: 'capTaxes',
      label: 'Taxes',
      value: capitalizedTaxes
    }, {
      key: 'capLenderFees',
      label: 'Lender Fees',
      value: capitalizedLenderFees
    }, {
      key: 'capGovFees',
      label: 'DMV Fees',
      value: capitalizedGovFees
    }, {
      key: 'capDealerFees',
      label: 'Dealer Fees',
      value: capitalizedDealerFees
    }];
    const diaTaxesFees = [{
      key: 'diaTaxes',
      label: 'Taxes',
      value: diaTaxes
    }, {
      key: 'diaLenderFees',
      label: 'Lender Fees',
      value: diaLenderFees
    }, {
      key: 'diaGovFees',
      label: 'DMV Fees',
      value: diaGovFees
    }, {
      key: 'diaDealerFees',
      label: 'Dealer Fees',
      value: diaDealerFees
    }];
    return isLease ? /*#__PURE__*/jsxs(Fragment, {
      children: [/*#__PURE__*/jsx("hr", {
        className: "margin-top-3"
      }), /*#__PURE__*/jsxs("div", {
        className: "text-size-400 margin-top-4",
        children: [!!totalDiaTaxesFees && /*#__PURE__*/jsxs("ul", {
          className: "list-unstyled",
          children: [/*#__PURE__*/jsxs(Text, {
            color: "primary",
            weight: "bold",
            children: [isEstimate ? 'Estimated ' : '', "Due at Signing"]
          }), renderLeaseFeesList(diaTaxesFees, rowClassnames), renderTotalTaxesFees('Total Due at Signing', totalDiaTaxesFees, rowClassnames)]
        }), !!capitalizedTaxesAndFees && /*#__PURE__*/jsxs("ul", {
          className: "margin-top-4 list-unstyled",
          children: [/*#__PURE__*/jsxs(Text, {
            color: "primary",
            weight: "bold",
            children: [isEstimate ? 'Estimated ' : '', "Financed"]
          }), renderLeaseFeesList(capTaxesFees, rowClassnames), renderTotalTaxesFees('Total Financed', capitalizedTaxesAndFees, rowClassnames)]
        })]
      })]
    }) : /*#__PURE__*/jsxs("div", {
      className: "text-size-400",
      children: [renderTotalTaxesFees('Total Taxes & Fees', showTotalTaxesOnly ? taxesFees?.totalTaxes : taxesFees.totalTaxesFees, rowClassnames), /*#__PURE__*/jsx("hr", {}), /*#__PURE__*/jsxs("ul", {
        className: "margin-top-4 list-unstyled",
        children: [Object.entries(taxesFees).map(_ref3 => {
          let [key, value] = _ref3;
          const label = key.replace(/([A-Z])/g, ' $1').replace(/^(.)/, s => s.toUpperCase());
          return key !== 'showTaxTotalOnly' && key !== 'totalTaxesFees' && key !== 'dealerFees' && value > 0 && /*#__PURE__*/jsxs("li", {
            className: rowClassnames,
            children: [/*#__PURE__*/jsx(Text, {
              children: `${label}`
            }), /*#__PURE__*/jsx(Text, {
              weight: "bold",
              children: `${formatCurrency(Math.floor(value))}`
            })]
          }, key);
        }), !!taxesFees.dealerFees && /*#__PURE__*/jsxs("li", {
          className: rowClassnames,
          children: [/*#__PURE__*/jsx(Text, {
            children: isEstimate ? 'Estimated Dealer Fees' : 'Dealer Fees'
          }), /*#__PURE__*/jsx(Text, {
            weight: "bold",
            children: taxesFees.dealerFees === 0 ? 'Check with dealer' : `${formatCurrency(Math.floor(taxesFees.dealerFees))}`
          })]
        }, "dealerFees")]
      })]
    });
  };
  return /*#__PURE__*/jsx(SideBarToModal, {
    "data-cmp": "taxes-fees-modal",
    title: "Taxes & Fees",
    show: showModal,
    container: container,
    content: /*#__PURE__*/jsxs(Fragment, {
      children: [/*#__PURE__*/jsx(Text, {
        color: "primary",
        weight: "bold",
        size: 400,
        children: "Confirm Your Location"
      }), /*#__PURE__*/jsx("p", {
        children: "Your location will be used to calculate taxes & fees and show more accurate payments."
      }), /*#__PURE__*/jsx("div", {
        className: "margin-top-4 row",
        children: /*#__PURE__*/jsx("div", {
          className: "col-xs-4",
          children: /*#__PURE__*/jsx(StandardZipInput, {
            label: "ZIP",
            name: "inputZip",
            value: zip,
            onChange: handleChange,
            required: true
          })
        })
      }), isLoading && /*#__PURE__*/jsx(LoadingSpinner, {
        className: "margin-vertical-4"
      }), !isLoading && showTotalTaxesOnly && renderFeesList(), !isLoading && !showTotalTaxesOnly && renderFeesList()]
    }),
    collapsed: isXs,
    modalOptions: {
      bsStyle: 'sm'
    },
    sideBarOptions: {
      fullWidth: true,
      action: continueButton
    },
    footerContent: continueButton,
    onHide: onHide
  });
}
export { TaxesFeesModal as default };
